import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkspaceNotice } from '@/components/ui/workspace';
import {
  useDocumentStatusQuery,
  useReindexDocumentMutation,
  useReprocessDocumentMutation,
  type DocumentListItem,
} from '@/lib/api/documents';
import { DeleteDocumentControl } from './delete-document-control';

/**
 * T-0160 — what is wrong with a document that is not usable, and what the
 * user can do about it, under its row on the Documents screen.
 *
 * Shown for the documents the screen used to hide (it listed classified
 * documents only):
 *   - `failed` — the read (parse) or the indexing failed. The reason comes
 *     from `GET /documents/:id/status`. Retry re-reads the stored file
 *     (`:reprocess`) when the read failed, or re-queues the failed chunks
 *     (`:reindex failed_only`) when indexing failed. Delete soft-deletes it.
 *   - `uploading` — the upload has not finished: still running, or
 *     interrupted (the bytes are not all on the server, so it cannot be
 *     read). If interrupted, upload the file again; Delete clears the entry.
 *
 * Authorization is the endpoints' own: each checks the caller's access to
 * the engagement (an off-engagement caller gets 404). No new privileged
 * route; a refusal is shown as returned.
 */
export interface DocumentIssueProps {
  engagementId: string;
  document: DocumentListItem;
}

/** Does this document need the issue line? */
export function documentHasIssue(document: DocumentListItem | null | undefined): boolean {
  return document?.process_status === 'failed' || document?.process_status === 'uploading';
}

export function DocumentIssue({ engagementId, document }: DocumentIssueProps) {
  const failed = document.process_status === 'failed';
  const status = useDocumentStatusQuery(failed ? document.id : undefined);
  const reprocess = useReprocessDocumentMutation(engagementId);
  const reindex = useReindexDocumentMutation(engagementId);
  const [retried, setRetried] = useState(false);

  const indexFailed =
    status.data?.parse_status === 'parsed' &&
    (status.data.index_status === 'failed' || status.data.index_status === 'partial');
  const reason = status.data?.failure_reason?.trim() || null;
  const retry = indexFailed ? reindex : reprocess;
  const retryError = retry.isError ? retry.error : null;

  const message = !failed
    ? 'Upload not finished. If it was interrupted, upload the file again or delete this entry.'
    : status.data === undefined
      ? 'Processing failed.'
      : indexFailed
        ? `Indexing failed${reason ? `: ${reason}` : '.'}`
        : `Could not read this file${reason ? `: ${reason}` : '.'}`;

  return (
    <div className="flex flex-wrap items-center gap-2 text-meta" data-testid="document-issue">
      <span className={failed ? 'text-posture-critical' : 'text-ink-700'}>{message}</span>
      {failed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={retry.isPending || retried}
          onClick={() => {
            retry.mutate(document.id, { onSuccess: () => setRetried(true) });
          }}
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
          <span>
            {retry.isPending
              ? 'Retrying…'
              : retried
                ? indexFailed
                  ? 'Indexing again…'
                  : 'Reading again…'
                : 'Retry'}
          </span>
        </Button>
      ) : null}
      <DeleteDocumentControl
        engagementId={engagementId}
        documentId={document.id}
        onDeleted={() => {
          // The documents list refetches on delete; the row goes with it.
        }}
        renderRequirementsLink={(label) => (
          <Link
            to="/engagements/$engagementId/selection"
            params={{ engagementId }}
            className="font-medium underline underline-offset-[3px]"
          >
            {label}
          </Link>
        )}
      />
      {retryError ? (
        <WorkspaceNotice tone="critical" className="basis-full">
          {retryError instanceof Error && retryError.message
            ? retryError.message
            : 'The retry could not be started.'}
        </WorkspaceNotice>
      ) : null}
    </div>
  );
}
